import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import { App, AppRegistry, ArrayApp, ModelRegistry, Input } from 'core/index';

import { FormStore } from 'libs/forms/app';

import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { PermissionedModel } from 'apps/permissions/app';
import { ContentModel, ContentApp } from 'apps/content/app';
import { PostCardLayout } from 'apps/aggregator/app';
import { ThreeColumnLayout, SmallThreeColumnLayout } from 'apps/layout';
import { raiseUserError } from 'apps/utils';

import { conf } from 'apps/forum/config.yaml';
import { conf as categoryConf } from 'apps/forum/categoryForm/config.yaml';

export class CategoryModel extends PermissionedModel {
  static registryName = 'CategoryModel';
  static ModelProps = {
    lastActiveThread: 'ThreadModel',
    forum: 'ForumModel',
    pinned: ['ThreadModel'],
  };

  @observable id;
  @observable forumId;
  @observable title = '';
  @observable description = '';
  @observable threadsCount;
  @observable privateThreadsCount;
  @observable color = '';
  @observable lastActiveThread = null;
  @observable urlId = '';
  @observable pinned = [];
}
ModelRegistry.add(CategoryModel);

export class CategoryApp extends App {
  static registryName = 'CategoryApp';
  static Model = CategoryModel;
  static AppProps = {
    lastActiveThread: 'ThreadApp',
    pinned: 'ThreadsApp',
  };

  createForm() {
    this.form = new FormStore(this, categoryConf, 'categoryForm');
  }
}
AppRegistry.add(CategoryApp);

export class CategoriesApp extends ArrayApp {
  static registryName = 'CategoriesApp';
  static ElementApp = CategoryApp;
  cardLayout = new SmallThreeColumnLayout();
}
AppRegistry.add(CategoriesApp);

export class ThreadModel extends PermissionedModel {
  static registryName = 'ThreadModel';
  static ModelProps = {
    creatorProfile: 'ProfileModel',
    category: 'CategoryModel',
    content: 'ContentModel',
    forum: 'ForumModel',
    parentContent: 'ContentModel',
  };

  @observable id;
  @observable membersOnly = false;
  @observable title = '';
  @observable msg = '';
  @observable creatorProfile = null;
  @observable category = null;
  @observable categoryId = '';
  @observable urlId = '';
  @observable tags = [];
  @observable dateCreated = '';
  @observable lastUpdated = '';
  @observable lastActivityAt;
  @observable comments = null;
  @observable commentsCount = 0;
  @observable commenters = [];
  @observable forum = null;
  @observable content = null;
  @observable isPinned = false;
  @observable canPin = false;
  @observable snippet = '';
  @observable parentContent = null;
}
ModelRegistry.add(ThreadModel);

export class ThreadApp extends App {
  static registryName = 'ThreadApp';
  static AppProps = {
    creatorProfile: 'ProfileApp',
    category: CategoryApp,
    content: 'ContentApp',
    forum: 'ForumApp',
    parentContent: 'ContentApp',
  };

  static Model = ThreadModel;
  @observable editing = false;
  @observable upvote;
  @observable undoIsHovered = false;
  @observable isEnable =
    clientStore.routerStore.params.get('initiative') === '+e-NABLE';

  @computed get route() {
    if (!(this.urlId && this.forum && this.forum.parentContent)) return {};
    return {
      name: 'initiative.forum.thread',
      params: {
        initiative: `+${this.forum.parentContent.slug}`,
        threadId: this.urlId,
      },
    };
  }

  createThreadForm(isMember) {
    if (isMember && this.isEnable) {
      this.form = new FormStore(this, conf, 'threadFormEnable');
    } else {
      this.form = new FormStore(this, conf, 'threadForm');
    }
  }

  createCategoryThreadForm(isMember) {
    if (isMember && this.isEnable) {
      this.form = new FormStore(this, conf, 'categoryThreadFormEnable');
    } else {
      this.form = new FormStore(this, conf, 'categoryThreadForm');
    }
  }

  @action
  EditThread = () => {
    this.createThreadForm();
    this.editing = true;
  };

  @action
  Cancel = () => {
    this.ResetLocals();
    this.editing = false;
    this.editFormStore = null;
  };

  @action
  SaveThread = () => {
    if (this.form.isValid) {
      this.form.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.UpdateThread({
        onSysErr: this.OnSaveError,
        onUserErr: this.OnSaveError,
        onSuccess: this.OnSaveSuccess,
      });
    } else {
      this.form.alert();
    }
  };

  @action
  OnSaveError = error => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
    this.ResetLocals();
    raiseUserError(error, 'delete', 'Thread');
  };

  @action
  OnSaveSuccess = () => {
    this.form.SetPosting(false);
    this.form.clearDraft();
    clientStore.SetPosting(false);
    this.editing = false;
  };

  @action
  Delete = () => {
    const input = new Input({
      id: this.id,
      deleted: true,
    });
    this.manager.DeleteThread(input, {
      onSysErr: this.OnDeleteErr,
      onUserErr: this.OnDeleteErr,
      onSuccess: this.OnDeleteSuccess,
    });
  };

  @action
  OnDeleteErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'delete', 'Thread');
  };

  @action
  OnDeleteSuccess = () => {
    const { routerStore } = clientStore;
    if (routerStore.route.name.includes('category')) {
      routerStore.navigate('initiative.forum', {
        initiative: routerStore.params.get('initiative'),
      });
    } else {
      routerStore.navigate('initiative.forum.category', {
        initiative: routerStore.params.get('initiative'),
        categoryId: this.category.urlId,
      });
    }
    this.ResetLocals();
    modalStore.ClearComponent();
    Alert.success('Thread has been deleted successfully.');
  };

  @action
  Submit = () => {
    if (this.form.isValid) {
      this.form.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.Submit({
        onSysErr: this.onSubmitSysErr,
        onUserErr: this.onSubmitUserErr,
        onSuccess: this.onSubmitSuccess,
      });
    } else {
      this.form.alert();
    }
  };

  @action
  onSubmitSysErr = error => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'post', 'Thread');
  };

  @action
  onSubmitUserErr = () => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
  };

  @action
  onSubmitSuccess = () => {
    this.form.SetPosting(false);
    this.form.clearDraft();
    clientStore.SetPosting(false);
    modalStore.ClearComponent();
    // clientStore.routerStore.navigate('forum.thread', {
    //   slug: result.slug
    // });
  };

  @action
  SetPinned = bool => {
    this.isPinned = bool;
    const input = new Input({
      categoryId: this.category.id,
      threadId: this.id,
      pin: bool,
    });
    this.manager.PinThread(input, {
      onSysErr: this.onPinErr,
      onUserErr: this.onPinErr,
      onSuccess: this.onPinSuccess,
    });
  };

  @action
  onPinErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'pin', 'Thread');
  };

  @action
  onPinSuccess = result => {
    this.ResetLocals();
    if (result.isPinned) {
      Alert.success('Thread pinned successfully.');
    } else {
      Alert.success('Thread un-pinned successfully.');
    }
    const manager =
      clientStore.context.get('forumAppManager') ||
      clientStore.context.get('categoryAppManager');
    manager.NewVariables();
  };
}
AppRegistry.add(ThreadApp);

export class ThreadsApp extends ArrayApp {
  static registryName = 'ThreadsApp';
  static ElementApp = ThreadApp;
  cardLayout = new PostCardLayout();
}
AppRegistry.add(ThreadsApp);

export class ForumModel extends PermissionedModel {
  static registryName = 'ForumModel';
  static ModelProps = {
    pinned: [ThreadModel],
    categories: [CategoryModel],
    parentContent: ContentModel,
  };

  @observable id;
  @observable name = '';
  @observable isMember = true;
  @observable description = '';
  @observable pinned = [];
  @observable categories = [];
  @observable privateThreadsCount;
  @observable parentContent = null;
}
ModelRegistry.add(ForumModel);

export class ForumApp extends App {
  static registryName = 'ForumApp';
  static AppProps = {
    pinned: ThreadsApp,
    categories: CategoriesApp,
    parentContent: ContentApp,
  };

  static Model = ForumModel;
  createForm() {
    this.form = new FormStore(this, conf, 'activationForm');
  }

  @computed get route() {
    if (!this.parentContent) return {};
    return {
      name: 'initiative.forum',
      params: {
        initiative: `+${this.parentContent.slug}`,
      },
    };
  }
}
AppRegistry.add(ForumApp);

export class ForumsApp extends ArrayApp {
  static registryName = 'ForumsApp';
  static ElementApp = ForumApp;
  cardLayout = new ThreeColumnLayout();
}
